import type { BackgammonSocket } from '@qlashgeminiz/qgammon-board-ui';

import type { QcpMatchSocket } from '@/hooks/useQcpMatch';
import { azioneAttesa } from '@/lib/qgammon/turno';
import type { ActiveMatchDescriptor } from '@/store/activeMatch.slice';

/** Game-agnostic snapshot the pill reads (no game-specific fields leak out). */
export interface ActiveMatchProjection {
  phase: 'yourTurn' | 'waiting' | 'ended';
  /**
   * Che cosa devo fare, quando lo sappiamo. Serve alla pastiglia per dire l'AZIONE invece del
   * solo «tocca a te»: [Luca, 14/09] *«vedo opponents turn quando invece devo fare roll»* — la
   * frase con cui ha descritto il difetto contiene anche cio' che si aspettava di leggere.
   * ⛔ Opzionale: chi non lo sa non lo inventa, e la pastiglia ripiega sul testo generico.
   */
  action?: 'roll' | 'move' | 'cube';
  /** Optional clock of the player currently on the move, in whole seconds. */
  clock?: { seconds: number; owner: 'me' | 'opponent' };
}

/**
 * QGammon → projection. `ended` vince; altrimenti si chiede a `azioneAttesa` CHE COSA deve fare
 * il giocatore, e «tocca a te» e' semplicemente «c'e' qualcosa da fare».
 *
 * 🔴 **Questa riga diceva `game.phase === 'moving'`, e per questo annunciava «turno
 * dell'avversario» a chi doveva TIRARE** [Luca, 14/09]. Il predicato non era sbagliato: era piu'
 * STRETTO del suo nome. ⭐ *«E' il mio turno?» era implementato come «sto muovendo?», e i due
 * coincidono ovunque tranne nel caso in cui l'avviso serve davvero* — cioe' quando l'utente ha
 * chiuso l'app **proprio perche' stava aspettando l'avversario**.
 * ⚠️ E non bastava aggiungere `rolling`: in `cubeOffered` agisce **chi NON ha il turno**. La
 * regola sta ora in `lib/qgammon/turno.ts`, con la sua tabella di verita'.
 */
export function projectQgammon(
  sock: BackgammonSocket,
  descriptor: ActiveMatchDescriptor,
): ActiveMatchProjection {
  if (sock.ended) return { phase: 'ended' };
  const game = sock.state?.game ?? null;
  const azione = azioneAttesa(game, descriptor.perspective ?? null);
  return azione ? { phase: 'yourTurn', action: azione } : { phase: 'waiting' };
}

/**
 * QCP → projection. `matchResult` (set end) wins; otherwise "yourTurn" when the
 * server-authoritative acting seat (`state.public.turn`) is ours. QCP placement
 * is strictly alternating — the gameserver's `GameSession.apply()` flips
 * `public.turn` to the other seat on every accepted placement and rebroadcasts
 * `match:state` — so `turn` is reliable where `draw` is not: `draw` is only set
 * on the Deal event and cleared on HandStart, NOT on `place()`, so it stays
 * populated while we're actually waiting on the opponent. Gated on `draw.length
 * > 0` too so a state/deal race (turn flips to us before our fresh Deal arrives)
 * conservatively reports "waiting" rather than a false "yourTurn".
 */
export function projectQcp(sock: QcpMatchSocket): ActiveMatchProjection {
  if (sock.matchResult) return { phase: 'ended' };
  const myTurn =
    sock.mySeat != null &&
    sock.state?.public.turn === sock.mySeat &&
    sock.draw.length > 0;
  return { phase: myTurn ? 'yourTurn' : 'waiting' };
}
